import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  ArrowDown,
  ArrowUp,
  Braces,
  Copy,
  Image as ImageIcon,
  Lock,
  MousePointerSquareDashed,
  Trash2,
  Unlock,
} from "lucide-react";

import type {
  EtiquetaElemento,
  FonteZpl,
  Rotacao,
  Simbologia,
} from "@/types/etiqueta-designer";
import { dotsParaMm, mmParaDots, type ContextoEtiqueta } from "@/utils/zplDesigner";
import { GRUPOS_VARIAVEIS } from "@/utils/variaveisZpl";

type Props = {
  elemento: EtiquetaElemento | null;
  ctx: ContextoEtiqueta;
  onPatch: (patch: Record<string, unknown>) => void;
  onExcluir: () => void;
  onDuplicar: () => void;
  onSubir: () => void;
  onDescer: () => void;
  onCentralizar: (eixo: "h" | "v") => void;
  onTrocarImagem: () => void;
};

function CampoNumero({
  id,
  rotulo,
  valor,
  onChange,
  passo = 0.5,
  min,
  max,
  sufixo,
}: {
  id: string;
  rotulo: string;
  valor: number;
  onChange: (v: number) => void;
  passo?: number;
  min?: number;
  max?: number;
  sufixo?: string;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-[11px] text-muted-foreground">
        {rotulo}
        {sufixo ? ` (${sufixo})` : ""}
      </Label>
      <Input
        id={id}
        type="number"
        className="h-8 text-xs"
        step={passo}
        min={min}
        max={max}
        value={Number.isFinite(valor) ? valor : 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      />
    </div>
  );
}

export function SeletorVariavel({
  onInserir,
  rotulo = "Variável",
}: {
  onInserir: (token: string) => void;
  rotulo?: string;
}) {
  const [busca, setBusca] = useState("");
  const termo = busca.trim().toLowerCase();
  const grupos = termo
    ? GRUPOS_VARIAVEIS.map((g) => ({
        ...g,
        variaveis: g.variaveis.filter(
          (v) =>
            v.nome.toLowerCase().includes(termo) ||
            v.id.toLowerCase().includes(termo),
        ),
      })).filter((g) => g.variaveis.length)
    : GRUPOS_VARIAVEIS;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1 text-[11px]">
          <Braces className="h-3.5 w-3.5" />
          {rotulo}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <Input
          autoFocus
          placeholder="Buscar variável..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="h-8 text-xs mb-2"
        />
        <div className="max-h-64 overflow-y-auto space-y-2">
          {grupos.map((g) => (
            <div key={g.titulo}>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-1 mb-1">
                {g.titulo}
              </p>
              <div className="flex flex-wrap gap-1">
                {g.variaveis.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    title={v.id}
                    onClick={() => onInserir(v.id)}
                    className="rounded border px-1.5 py-0.5 text-[10px] hover:bg-accent"
                  >
                    {v.nome}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!grupos.length && (
            <p className="text-xs text-muted-foreground px-1 py-4 text-center">
              Nenhuma variável encontrada.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function PainelPropriedades({
  elemento,
  ctx,
  onPatch,
  onExcluir,
  onDuplicar,
  onSubir,
  onDescer,
  onCentralizar,
  onTrocarImagem,
}: Props) {
  if (!elemento) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground">
        <MousePointerSquareDashed className="h-8 w-8 opacity-40" />
        <p className="text-xs">
          Selecione um elemento no canvas para editar as propriedades.
        </p>
      </div>
    );
  }

  const el = elemento;

  return (
    <div className="space-y-3 p-3">
      {/* Ações */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={onSubir}
          title="Trazer para frente"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={onDescer}
          title="Enviar para trás"
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={() => onCentralizar("h")}
          title="Centralizar na horizontal"
        >
          <AlignCenterVertical className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={() => onCentralizar("v")}
          title="Centralizar na vertical"
        >
          <AlignCenterHorizontal className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={onDuplicar}
          title="Duplicar (Ctrl+D)"
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={() => onPatch({ bloqueado: !el.bloqueado })}
          title={el.bloqueado ? "Desbloquear" : "Bloquear"}
        >
          {el.bloqueado ? (
            <Lock className="h-3.5 w-3.5" />
          ) : (
            <Unlock className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7 ml-auto text-red-600 hover:text-red-700 hover:bg-red-500/10"
          onClick={onExcluir}
          title="Excluir (Del)"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <Separator />

      {/* Comuns */}
      <div className="grid grid-cols-2 gap-2">
        <CampoNumero
          id="prop-x"
          rotulo="X"
          sufixo="mm"
          valor={el.x}
          onChange={(v) => onPatch({ x: v })}
        />
        <CampoNumero
          id="prop-y"
          rotulo="Y"
          sufixo="mm"
          valor={el.y}
          onChange={(v) => onPatch({ y: v })}
        />
      </div>

      {el.tipo !== "bruto" && (
        <div className="grid gap-1.5">
          <Label className="text-[11px] text-muted-foreground">Rotação</Label>
          <ToggleGroup
            type="single"
            size="sm"
            value={String(el.rotacao)}
            onValueChange={(v) => v && onPatch({ rotacao: Number(v) as Rotacao })}
            className="justify-start"
          >
            {[0, 90, 180, 270].map((g) => (
              <ToggleGroupItem
                key={g}
                value={String(g)}
                className="h-7 px-2 text-[11px]"
              >
                {g}°
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      <Separator />

      {/* Específicos */}
      {el.tipo === "texto" && (
        <div className="space-y-3">
          <div className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] text-muted-foreground">
                Conteúdo
              </Label>
              <SeletorVariavel
                onInserir={(t) => onPatch({ texto: (el.texto || "") + t })}
              />
            </div>
            <Textarea
              className="text-xs h-20 resize-none"
              value={el.texto}
              onChange={(e) => onPatch({ texto: e.target.value })}
              spellCheck={false}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-1.5">
              <Label className="text-[11px] text-muted-foreground">Fonte</Label>
              <Select
                value={el.fonte}
                onValueChange={(v) => onPatch({ fonte: v as FonteZpl })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 — escalável</SelectItem>
                  <SelectItem value="A">A — 9x5</SelectItem>
                  <SelectItem value="B">B — 11x7</SelectItem>
                  <SelectItem value="D">D — 18x10</SelectItem>
                  <SelectItem value="E">E — OCR-B</SelectItem>
                  <SelectItem value="F">F — 26x13</SelectItem>
                  <SelectItem value="G">G — 60x40</SelectItem>
                  <SelectItem value="H">H — OCR-A</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Switch
                id="prop-inverso"
                checked={!!el.inverso}
                onCheckedChange={(v) => onPatch({ inverso: v })}
              />
              <Label htmlFor="prop-inverso" className="text-[11px]">
                Invertido
              </Label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <CampoNumero
              id="prop-alt-fonte"
              rotulo="Altura"
              sufixo="mm"
              min={0.5}
              valor={el.alturaFonte}
              onChange={(v) => onPatch({ alturaFonte: v })}
            />
            <CampoNumero
              id="prop-larg-fonte"
              rotulo="Largura"
              sufixo="mm"
              min={0.5}
              valor={el.larguraFonte}
              onChange={(v) => onPatch({ larguraFonte: v })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <CampoNumero
              id="prop-bloco"
              rotulo="Bloco (0 = livre)"
              sufixo="mm"
              min={0}
              valor={el.larguraBloco ?? 0}
              onChange={(v) =>
                onPatch({ larguraBloco: v, maxLinhas: el.maxLinhas || 3 })
              }
            />
            <CampoNumero
              id="prop-linhas"
              rotulo="Máx. linhas"
              passo={1}
              min={1}
              valor={el.maxLinhas ?? 1}
              onChange={(v) => onPatch({ maxLinhas: Math.max(1, v) })}
            />
          </div>
          {!!el.larguraBloco && (
            <div className="grid gap-1.5">
              <Label className="text-[11px] text-muted-foreground">
                Alinhamento
              </Label>
              <ToggleGroup
                type="single"
                size="sm"
                value={el.alinhamento || "L"}
                onValueChange={(v) => v && onPatch({ alinhamento: v })}
                className="justify-start"
              >
                <ToggleGroupItem value="L" className="h-7 px-2 text-[11px]">
                  Esq.
                </ToggleGroupItem>
                <ToggleGroupItem value="C" className="h-7 px-2 text-[11px]">
                  Centro
                </ToggleGroupItem>
                <ToggleGroupItem value="R" className="h-7 px-2 text-[11px]">
                  Dir.
                </ToggleGroupItem>
                <ToggleGroupItem value="J" className="h-7 px-2 text-[11px]">
                  Just.
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}
        </div>
      )}

      {el.tipo === "barras" && (
        <div className="space-y-3">
          <div className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] text-muted-foreground">Dados</Label>
              <SeletorVariavel
                onInserir={(t) => onPatch({ dados: (el.dados || "") + t })}
              />
            </div>
            <Input
              className="h-8 text-xs"
              value={el.dados}
              onChange={(e) => onPatch({ dados: e.target.value })}
              spellCheck={false}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-[11px] text-muted-foreground">
              Simbologia
            </Label>
            <Select
              value={el.simbologia}
              onValueChange={(v) => onPatch({ simbologia: v as Simbologia })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CODE128">Code 128</SelectItem>
                <SelectItem value="CODE39">Code 39</SelectItem>
                <SelectItem value="CODE93">Code 93</SelectItem>
                <SelectItem value="EAN13">EAN-13</SelectItem>
                <SelectItem value="EAN8">EAN-8</SelectItem>
                <SelectItem value="UPCA">UPC-A</SelectItem>
                <SelectItem value="ITF">Intercalado 2 de 5</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <CampoNumero
              id="prop-bar-altura"
              rotulo="Altura"
              sufixo="mm"
              min={1}
              valor={el.altura}
              onChange={(v) => onPatch({ altura: v })}
            />
            <CampoNumero
              id="prop-bar-modulo"
              rotulo="Módulo"
              sufixo="dots"
              passo={1}
              min={1}
              max={10}
              valor={el.modulo}
              onChange={(v) =>
                onPatch({ modulo: Math.min(10, Math.max(1, Math.round(v))) })
              }
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="prop-bar-texto"
                checked={el.mostrarTexto}
                onCheckedChange={(v) => onPatch({ mostrarTexto: v })}
              />
              <Label htmlFor="prop-bar-texto" className="text-[11px]">
                Mostrar texto
              </Label>
            </div>
            {el.mostrarTexto && (
              <div className="flex items-center gap-2">
                <Switch
                  id="prop-bar-acima"
                  checked={el.textoAcima}
                  onCheckedChange={(v) => onPatch({ textoAcima: v })}
                />
                <Label htmlFor="prop-bar-acima" className="text-[11px]">
                  Acima
                </Label>
              </div>
            )}
          </div>
        </div>
      )}

      {el.tipo === "qrcode" && (
        <div className="space-y-3">
          <div className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] text-muted-foreground">
                Conteúdo
              </Label>
              <SeletorVariavel
                onInserir={(t) => onPatch({ dados: (el.dados || "") + t })}
              />
            </div>
            <Textarea
              className="text-xs h-16 resize-none"
              value={el.dados}
              onChange={(e) => onPatch({ dados: e.target.value })}
              spellCheck={false}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <CampoNumero
              id="prop-qr-mag"
              rotulo="Magnificação"
              passo={1}
              min={1}
              max={10}
              valor={el.magnificacao}
              onChange={(v) =>
                onPatch({
                  magnificacao: Math.min(10, Math.max(1, Math.round(v))),
                })
              }
            />
            <div className="grid gap-1.5">
              <Label className="text-[11px] text-muted-foreground">
                Correção
              </Label>
              <Select
                value={el.correcao}
                onValueChange={(v) => onPatch({ correcao: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">L — 7%</SelectItem>
                  <SelectItem value="M">M — 15%</SelectItem>
                  <SelectItem value="Q">Q — 25%</SelectItem>
                  <SelectItem value="H">H — 30%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {el.tipo === "imagem" && (
        <div className="space-y-3">
          <div className="rounded-md border bg-muted/40 p-2 text-[11px] text-muted-foreground">
            {el.larguraDots} × {el.alturaDots} dots ·{" "}
            {dotsParaMm(el.larguraDots, ctx.dpmm)} ×{" "}
            {dotsParaMm(el.alturaDots, ctx.dpmm)} mm
          </div>
          <div className="grid grid-cols-2 gap-2">
            <CampoNumero
              id="prop-img-larg"
              rotulo="Largura"
              sufixo="mm"
              min={1}
              valor={dotsParaMm(el.larguraDots, ctx.dpmm)}
              onChange={(v) =>
                onPatch({
                  __redimensionarImagem: {
                    largura: mmParaDots(v, ctx.dpmm),
                    manterProporcao: true,
                  },
                })
              }
            />
            <CampoNumero
              id="prop-img-alt"
              rotulo="Altura"
              sufixo="mm"
              min={1}
              valor={dotsParaMm(el.alturaDots, ctx.dpmm)}
              onChange={(v) =>
                onPatch({
                  __redimensionarImagem: {
                    altura: mmParaDots(v, ctx.dpmm),
                    manterProporcao: true,
                  },
                })
              }
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-1"
            onClick={onTrocarImagem}
          >
            <ImageIcon className="h-3.5 w-3.5" />
            Substituir / reprocessar imagem
          </Button>
        </div>
      )}

      {(el.tipo === "caixa" || el.tipo === "linha") && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <CampoNumero
              id="prop-cx-larg"
              rotulo="Largura"
              sufixo="mm"
              min={0}
              valor={el.largura}
              onChange={(v) => onPatch({ largura: v })}
            />
            <CampoNumero
              id="prop-cx-alt"
              rotulo="Altura"
              sufixo="mm"
              min={0}
              valor={el.altura}
              onChange={(v) => onPatch({ altura: v })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <CampoNumero
              id="prop-cx-esp"
              rotulo="Espessura"
              sufixo="dots"
              passo={1}
              min={1}
              valor={el.espessura}
              onChange={(v) => onPatch({ espessura: Math.max(1, Math.round(v)) })}
            />
            <CampoNumero
              id="prop-cx-round"
              rotulo="Arredondar"
              passo={1}
              min={0}
              max={8}
              valor={el.arredondamento}
              onChange={(v) =>
                onPatch({
                  arredondamento: Math.min(8, Math.max(0, Math.round(v))),
                })
              }
            />
          </div>
          <div className="flex items-center gap-4">
            {el.tipo === "caixa" && (
              <div className="flex items-center gap-2">
                <Switch
                  id="prop-cx-fill"
                  checked={!!el.preenchido}
                  onCheckedChange={(v) => onPatch({ preenchido: v })}
                />
                <Label htmlFor="prop-cx-fill" className="text-[11px]">
                  Preenchido
                </Label>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                id="prop-cx-inv"
                checked={!!el.inverso}
                onCheckedChange={(v) => onPatch({ inverso: v })}
              />
              <Label htmlFor="prop-cx-inv" className="text-[11px]">
                Branco
              </Label>
            </div>
          </div>
        </div>
      )}

      {el.tipo === "bruto" && (
        <div className="grid gap-1.5">
          <Label className="text-[11px] text-muted-foreground">
            ZPL deste bloco
          </Label>
          <Textarea
            className="h-40 text-[11px] font-mono resize-none"
            value={el.zpl}
            onChange={(e) => onPatch({ zpl: e.target.value })}
            spellCheck={false}
          />
          <p className="text-[10px] text-muted-foreground">
            Bloco não interpretado pelo editor visual. É enviado à impressora
            exatamente como está.
          </p>
        </div>
      )}

      <Separator />

      <div className="grid gap-1.5">
        <Label htmlFor="prop-nome" className="text-[11px] text-muted-foreground">
          Nome do elemento (opcional)
        </Label>
        <Input
          id="prop-nome"
          className="h-8 text-xs"
          value={el.nome ?? ""}
          placeholder="Ex: Logo do cliente"
          onChange={(e) => onPatch({ nome: e.target.value })}
        />
      </div>
    </div>
  );
}
